import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import roadmapService, { RoadmapSection, RoadmapItem } from '../services/roadmap.service';
import {
  MONTH_ABBREVS,
  MONTH_NAMES,
  compareSpans,
  monthIndex,
  roadmapYears,
  spanLabel,
  todayPosition,
} from '../utils/roadmapTimeline';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUARTER_LABELS = [
  { label: 'Q1 · Jan – Mar', color: '#93c5fd' },
  { label: 'Q2 · Apr – Jun', color: '#6ee7b7' },
  { label: 'Q3 · Jul – Sep', color: '#fcd34d' },
  { label: 'Q4 · Oct – Dec', color: '#f9a8d4' },
];

const STATUS_CONFIG: Record<string, { dot: string; bg: string; border: string; text: string; label: string; barBg: string; barBorder: string; barText: string; dashed?: boolean }> = {
  live: {
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    border: 'border-emerald-300 dark:border-emerald-700',
    text: 'text-emerald-700 dark:text-emerald-300',
    label: 'Live',
    barBg: '#dcfce7',
    barBorder: '#86efac',
    barText: '#166534',
    dashed: false,
  },
  progress: {
    dot: 'bg-blue-500',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    border: 'border-blue-300 dark:border-blue-700',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'In Progress',
    barBg: '#dbeafe',
    barBorder: '#93c5fd',
    barText: '#1e40af',
    dashed: false,
  },
  planned: {
    dot: 'bg-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    border: 'border-amber-300 dark:border-amber-700',
    text: 'text-amber-700 dark:text-amber-300',
    label: 'Planned',
    barBg: '#fef3c7',
    barBorder: '#fcd34d',
    barText: '#92400e',
    dashed: false,
  },
  tentative: {
    dot: 'bg-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800/60',
    border: 'border-slate-300 dark:border-slate-600',
    text: 'text-slate-500 dark:text-slate-400',
    label: 'Tentative',
    barBg: '#f1f5f9',
    barBorder: '#cbd5e1',
    barText: '#64748b',
    dashed: true,
  },
};

const DARK_BAR_COLORS: Record<string, { barBg: string; barBorder: string; barText: string }> = {
  live:      { barBg: 'rgba(6,78,59,0.45)',   barBorder: '#065f46', barText: '#6ee7b7' },
  progress:  { barBg: 'rgba(30,58,138,0.45)', barBorder: '#1e3a8a', barText: '#93c5fd' },
  planned:   { barBg: 'rgba(120,53,15,0.45)', barBorder: '#78350f', barText: '#fcd34d' },
  tentative: { barBg: 'rgba(51,65,85,0.45)',  barBorder: '#475569', barText: '#94a3b8' },
};

const LABEL_COL_WIDTH = 230; // px, fixed label column
const MONTH_COL_MIN_WIDTH = 56; // px; below this a one-month bar can't show its label

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Roadmap: React.FC = () => {
  const [sections, setSections] = useState<RoadmapSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; item: RoadmapItem | null; x: number; y: number }>({
    visible: false,
    item: null,
    x: 0,
    y: 0,
  });
  const [colWidth, setColWidth] = useState(0);
  const [isDark, setIsDark] = useState(false);

  const timelineRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --------------------------------------------------
  // Detect dark mode
  // --------------------------------------------------
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // --------------------------------------------------
  // Sort rows to match original HTML logic
  // --------------------------------------------------
  const STATUS_ORDER: Record<string, number> = { live: 0, progress: 1, planned: 2, tentative: 3 };

  const sortSectionRows = (rows: RoadmapItem[]): RoadmapItem[] => {
    const regular = rows.filter((r) => r.parent_id === null);
    const subs = rows.filter((r) => r.parent_id !== null);

    // Sort regular rows: start asc, end asc (across years), then status order
    const sorted = [...regular].sort((a, b) => {
      const spanDiff = compareSpans(a, b);
      if (spanDiff !== 0) return spanDiff;
      return (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    });

    // Insert each sub-feature right after its parent in the sorted list
    subs.forEach((sub) => {
      const parentIdx = sorted.findIndex((r) => r.id === sub.parent_id);
      if (parentIdx >= 0) {
        sorted.splice(parentIdx + 1, 0, sub);
      } else {
        sorted.push(sub);
      }
    });

    return sorted;
  };

  // --------------------------------------------------
  // Load data
  // --------------------------------------------------
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await roadmapService.getAll();
        // Apply the same sort logic as the original HTML
        const sortedData = data.map((section) => ({
          ...section,
          rows: sortSectionRows(section.rows),
        }));
        setSections(sortedData);
      } catch (err: any) {
        setError(err?.message || 'Failed to load roadmap data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // --------------------------------------------------
  // Years on the chart: this year and next, widened to whatever the items touch
  // --------------------------------------------------
  const years = useMemo(() => roadmapYears(sections.flatMap((s) => s.rows)), [sections]);
  const firstYear = years[0];
  const lastYear = years[years.length - 1];
  const totalMonths = years.length * 12;
  const chartMinWidth = LABEL_COL_WIDTH + totalMonths * MONTH_COL_MIN_WIDTH;

  // --------------------------------------------------
  // Measure column width on mount + resize
  // --------------------------------------------------
  const measureColumns = useCallback(() => {
    if (timelineRef.current) {
      const width = timelineRef.current.getBoundingClientRect().width;
      setColWidth(width / totalMonths);
    }
  }, [totalMonths]);

  useEffect(() => {
    measureColumns();
    window.addEventListener('resize', measureColumns);
    return () => window.removeEventListener('resize', measureColumns);
  }, [measureColumns, sections]);

  // Also measure after first paint with rAF
  useEffect(() => {
    const id = requestAnimationFrame(measureColumns);
    return () => cancelAnimationFrame(id);
  }, [measureColumns, sections]);

  // --------------------------------------------------
  // Today line position (null when today is off the chart)
  // --------------------------------------------------
  const todayPos = todayPosition(new Date(), firstYear, totalMonths);
  const todayPercent = todayPos === null ? null : (todayPos / totalMonths) * 100;

  // --------------------------------------------------
  // Tooltip handlers
  // --------------------------------------------------
  const handleMouseEnter = useCallback((item: RoadmapItem, e: React.MouseEvent) => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setTooltip({ visible: true, item, x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    tooltipTimerRef.current = setTimeout(() => {
      setTooltip({ visible: false, item: null, x: 0, y: 0 });
    }, 100);
  }, []);

  // --------------------------------------------------
  // Bar position helpers
  // --------------------------------------------------
  const getBarStyle = (item: RoadmapItem) => {
    if (colWidth === 0) return { display: 'none' as const };
    const startIdx = monthIndex(item.start_year, item.start_month, firstYear);
    const endIdx = monthIndex(item.end_year, item.end_month, firstYear);
    const padding = 4;
    const left = startIdx * colWidth + padding;
    const width = (endIdx - startIdx + 1) * colWidth - padding * 2;
    const cfg = isDark ? DARK_BAR_COLORS[item.status] : STATUS_CONFIG[item.status];
    const dashed = STATUS_CONFIG[item.status]?.dashed;
    return {
      position: 'absolute' as const,
      left: `${left}px`,
      width: `${width}px`,
      top: '50%',
      transform: 'translateY(-50%)',
      height: '22px',
      backgroundColor: cfg?.barBg || '#f1f5f9',
      border: `1.5px ${dashed ? 'dashed' : 'solid'} ${cfg?.barBorder || '#cbd5e1'}`,
      borderRadius: '4px',
      color: cfg?.barText || '#64748b',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '8px',
      paddingRight: '8px',
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      zIndex: 1,
    };
  };

  // --------------------------------------------------
  // Render helpers
  // --------------------------------------------------
  const renderTooltip = () => {
    if (!tooltip.visible || !tooltip.item) return null;
    const item = tooltip.item;
    const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.tentative;

    // Reposition to stay in viewport
    const ttWidth = 320;
    const ttHeight = 160;
    let x = tooltip.x + 16;
    let y = tooltip.y + 16;
    if (x + ttWidth > window.innerWidth - 16) x = tooltip.x - ttWidth - 16;
    if (y + ttHeight > window.innerHeight - 16) y = tooltip.y - ttHeight - 16;
    if (x < 8) x = 8;
    if (y < 8) y = 8;

    const endMonthName = `${MONTH_NAMES[item.end_month - 1]} ${item.end_year}`;

    return (
      <div
        className="fixed z-50 pointer-events-none transition-opacity duration-150"
        style={{ left: x, top: y, opacity: tooltip.visible ? 1 : 0 }}
      >
        <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-4 max-w-xs"
          style={{ fontFamily: 'inherit' }}
        >
          <div className="font-semibold text-body text-sm mb-2">{item.name}</div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.text} mb-2`}>
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`} />
            {cfg.label}
          </span>
          {item.status !== 'live' && (
            <div className="text-xs text-muted mt-1 mb-1">
              Launch expected near end of <span className="font-medium">{endMonthName}</span>
            </div>
          )}
          {item.description && (
            <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">{item.description}</div>
          )}
          {item.timeline_label && (
            <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {item.timeline_label}
            </div>
          )}
        </div>
      </div>
    );
  };

  // --------------------------------------------------
  // Loading / Error states
  // --------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" style={{ fontFamily: 'inherit' }}>
        <div className="flex items-center gap-3 text-muted">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading roadmap...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64" style={{ fontFamily: 'inherit' }}>
        <div className="text-red-500 dark:text-red-400 text-center">
          <div className="text-lg font-medium mb-1">Failed to load roadmap</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const yearRangeLabel = firstYear === lastYear ? `${firstYear}` : `${firstYear} – ${lastYear}`;

  // --------------------------------------------------
  // Main render
  // --------------------------------------------------
  return (
    <div className="p-4 md:p-6 min-h-full" style={{ fontFamily: 'inherit' }}>
      {/* ---- Header ---- */}
      <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold tracking-wide text-white"
            style={{ backgroundColor: 'rgb(var(--c-slate-800))' }}
          >
            gwi360
          </span>
          <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-gray-600" />
          <div>
            <h1 className="text-lg font-bold text-body">Technology Roadmap {yearRangeLabel}</h1>
            <p className="text-xs text-muted">
              Platform Transformation · January {firstYear} – December {lastYear} · Hover any initiative to learn more
            </p>
          </div>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap">
          {(['live', 'progress', 'planned', 'tentative'] as const).map((s) => {
            const cfg = STATUS_CONFIG[s];
            return (
              <div key={s} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                <span className={`w-2 h-2 rounded-sm ${cfg.dot}`} />
                {cfg.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* ---- Disclaimer ---- */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-3 mb-4 flex items-start gap-2.5">
        <svg className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
          Features are typically released <strong>near the end of their development window</strong> — don't expect to see changes at the start of a bar.{' '}
          <strong>Planned and Tentative timelines are subject to change.</strong>{' '}
          For questions or updates on any upcoming feature, reach out to the <strong>Innovation Team</strong>.
        </p>
      </div>

      {/* ---- Gantt Chart Card ---- */}
      <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-x-auto">
        {/* Inner wrapper with min width for horizontal scroll — grows with the number of months */}
        <div style={{ minWidth: `${chartMinWidth}px` }} className="relative">
          {/* Year header row */}
          <div className="flex" style={{ backgroundColor: '#111a33' }}>
            <div className="flex-shrink-0 border-r border-white/10" style={{ width: `${LABEL_COL_WIDTH}px` }} />
            <div className="flex flex-1">
              {years.map((year) => (
                <div key={year} className="flex-1 text-center py-1.5 text-xs font-bold tracking-widest text-white border-r border-white/10 last:border-r-0">
                  {year}
                </div>
              ))}
            </div>
          </div>

          {/* Quarter header row — repeats once per year */}
          <div className="flex" style={{ backgroundColor: '#1a2340' }}>
            <div className="flex-shrink-0 border-r border-white/10" style={{ width: `${LABEL_COL_WIDTH}px` }} />
            <div className="flex flex-1">
              {years.map((year) => QUARTER_LABELS.map((q) => (
                <div key={`${year}-${q.label}`} className="flex-1 text-center py-2.5 text-[0.6875rem] font-semibold tracking-widest uppercase border-r border-white/10 last:border-r-0"
                  style={{ color: q.color }}
                >
                  {q.label}
                </div>
              )))}
            </div>
          </div>

          {/* Month header row — with TODAY label */}
          <div className="flex border-b border-rule relative"
            style={{ backgroundColor: isDark ? '#1e293b' : '#f8f9fb' }}
          >
            <div className="flex-shrink-0 border-r border-rule flex items-center px-4"
              style={{ width: `${LABEL_COL_WIDTH}px` }}
            >
              <span className="text-[0.6875rem] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Initiative</span>
            </div>
            <div className="flex flex-1 relative" ref={timelineRef}>
              {years.map((year) => MONTH_ABBREVS.map((m) => (
                <div key={`${year}-${m}`} className="flex-1 text-center py-2 text-[0.6875rem] font-medium text-gray-400 dark:text-gray-500 tracking-wider border-r border-rule last:border-r-0">
                  {m}
                </div>
              )))}
              {/* TODAY label positioned in the month header */}
              {todayPercent !== null && (
                <div
                  className="absolute bottom-0 pointer-events-none z-20"
                  style={{ left: `${todayPercent}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="bg-blue-500 dark:bg-blue-400 text-white text-[0.5625rem] font-bold px-1.5 py-0.5 rounded-t-sm leading-none tracking-wider">
                    TODAY
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sections & rows — wrapped in relative container for today line overlay */}
          <div className="relative">
            {sections.map((section) => (
              <div key={section.section}>
                {/* Section header */}
                <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700"
                  style={{ backgroundColor: isDark ? '#334155' : '#eef0f4' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: section.section_color, boxShadow: `0 0 6px ${section.section_color}44` }} />
                  <span className="text-[0.65625rem] font-bold tracking-[0.1em] text-gray-500 dark:text-gray-300 uppercase">
                    {section.section}
                  </span>
                </div>

                {/* Rows */}
                {section.rows.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-stretch border-b border-gray-200 dark:border-gray-700/60 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors cursor-default group ${item.parent_id !== null ? 'bg-gray-50/80' : ''}`}
                    onMouseEnter={(e) => handleMouseEnter(item, e)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Label column */}
                    <div
                      className="flex items-center gap-2.5 flex-shrink-0 border-r border-gray-200 dark:border-gray-700/60"
                      style={{ width: `${LABEL_COL_WIDTH}px`, minHeight: '44px', padding: item.parent_id !== null ? '0 14px 0 32px' : '0 14px 0 16px' }}
                    >
                      {item.parent_id !== null ? (
                        <>
                          <span className="text-gray-300 dark:text-gray-600 text-xs select-none">└</span>
                          <span className={`w-[7px] h-[7px] rounded-sm flex-shrink-0 ${STATUS_CONFIG[item.status]?.dot || 'bg-gray-400'}`} />
                          <span className="text-xs text-muted truncate">{item.name}</span>
                        </>
                      ) : (
                        <>
                          <span className={`w-[7px] h-[7px] rounded-sm flex-shrink-0 ${STATUS_CONFIG[item.status]?.dot || 'bg-gray-400'}`} />
                          <span className="text-[0.78125rem] font-medium text-gray-800 dark:text-gray-200 truncate leading-tight">{item.name}</span>
                        </>
                      )}
                    </div>

                    {/* Timeline column */}
                    <div className="flex-1 relative" style={{ minHeight: '44px' }}>
                      {/* Month grid lines — a heavier rule where a new year begins */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {years.map((year) => MONTH_ABBREVS.map((m, i) => (
                          <div
                            key={`${year}-${m}`}
                            className={`flex-1 border-r last:border-r-0 ${i === 11 ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700/40'}`}
                          />
                        )))}
                      </div>

                      {/* Today line (per-row for correct alignment with timeline flex area) */}
                      {todayPercent !== null && (
                        <div
                          className="absolute top-0 bottom-0 pointer-events-none z-10"
                          style={{ left: `${todayPercent}%` }}
                        >
                          <div className="w-px h-full bg-blue-400 dark:bg-blue-500 opacity-60" />
                        </div>
                      )}

                      {/* Bar */}
                      {colWidth > 0 && (
                        <div style={getBarStyle(item)}>
                          <span className="truncate px-1">{spanLabel(item)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Footer ---- */}
      <div className="flex items-center justify-between mt-4 px-1 text-[0.6875rem] text-gray-400 dark:text-gray-500">
        <span>Last updated April 2026</span>
        <span>Gulf Winds International · Confidential · Internal Use Only</span>
      </div>

      {/* ---- Tooltip overlay ---- */}
      {renderTooltip()}
    </div>
  );
};

export default Roadmap;
